import { useState } from 'react';
import styled from 'styled-components';
import dayjs from 'dayjs';

import {
  IconAbsent,
  IconCheckInCircle,
  IconChevronDown,
  IconDefaultProfile,
  IconWarning,
} from 'assets/icons';

import ClipboardCopy from 'components/ClipboardCopy/ClipboardCopy';

import {
  BookingEdge,
  BookingState,
  FixedTicket,
  OrderTransactionActionStatus,
  TicketState,
  UnfixedTicket,
} from 'types/api';

import { formatPhoneNumber } from 'utils/formatting';

import ListViewBookedUserDetail from './ListViewBookedUserDetail';
import ProductStatusBadge from './ProductStatusBadge';
import ListLoadingSpinner from './ListLoadingSpinner';
import { useIsMobile } from 'hooks/common';
import Row from 'components/common/Row';
import ImgComponent from 'components/ImgComponent/ImgComponent';

const TicketBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;

  width: 100%;

  @media (max-width: 768px) {
    gap: 24px;
  }
`;

const MobileBriefInfoBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  width: 100%;

  cursor: pointer;

  > div {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 6px;

    width: 100%;
  }

  .period-box {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  p[data-type='title'] {
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;

    color: #70737c;
  }

  img {
    max-width: 54px;
    max-height: 54px;
    min-width: 54px;
    min-height: 54px;
    border-radius: 12px;

    margin-left: 16px;

    object-fit: cover;

    @media (max-width: 768px) {
      max-width: 50px;
      max-height: 50px;
      min-width: 50px;
      min-height: 50px;
    }
  }

  > svg {
    margin-right: auto;

    cursor: pointer;
  }
`;

const MobileDateContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;

  width: 100%;

  cursor: pointer;

  div[data-type='date-box'] {
    display: flex;
    gap: 4px;
    white-space: nowrap;
  }

  p[data-type='date'] {
    width: fit-content;
    margin-top: 2px;

    font-weight: 400;
    font-size: 14px;
    line-height: 20px;

    color: #46474c;
  }

  p[data-type='time'] {
    width: fit-content;

    font-weight: 600;
    font-size: 16px;
    line-height: 24px;

    color: #46474c;
  }
`;

const BriefInfoBox = styled.div`
  display: flex;
  align-items: center;

  width: 100%;

  cursor: pointer;

  > div {
    display: flex;
    flex-direction: column;
    gap: 4px;

    .period-box {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    p[data-type='date'] {
      margin-left: 4px;

      font-weight: 400;
      font-size: 14px;
      line-height: 20px;

      color: #46474c;
    }

    p[data-type='time'] {
      font-weight: 600;
      font-size: 18px;
      line-height: 26px;

      color: #46474c;
    }

    p[data-type='title'] {
      font-weight: 500;
      font-size: 16px;
      line-height: 22px;

      color: #70737c;
    }
  }

  > img {
    max-width: 56px;
    max-height: 56px;
    min-width: 56px;
    min-height: 56px;
    border-radius: 12px;

    margin-right: 16px;

    object-fit: cover;
  }

  > svg {
    margin-left: auto;

    cursor: pointer;
  }
`;

const UserListBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const BookingOverviewBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  margin-bottom: 4px;

  .booking-count-box {
    font-weight: 500;
    font-size: 15px;
    line-height: 22px;

    color: #46474c;

    &.searched {
      color: #0071e9;
    }

    > span {
      font-weight: 500;
      font-size: 16px;
      line-height: 22px;
      color: #eaebec;
    }
  }

  .detail-view-box {
    display: flex;
    align-items: center;
    gap: 4px;

    font-weight: 600;
    font-size: 16px;
    line-height: 24px;

    color: #0071e9;

    cursor: pointer;

    > svg {
      transform: rotate(-90deg);
    }
  }
`;

const UserCardBox = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;

  padding: 20px 16px;
  border-radius: 12px;

  background-color: #f7f7f8;
`;

const UserBriefInfoBox = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  width: 100%;

  cursor: pointer;

  > img[data-type='thumbnail'] {
    min-width: 52px;
    min-height: 52px;
    max-width: 52px;
    max-height: 52px;

    border-radius: 16px;
  }

  .default-profile-box {
    min-width: 52px;
    min-height: 52px;
    max-width: 52px;
    max-height: 52px;
  }
`;

const ChevronBox = styled.div<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;

  margin-left: auto;
  cursor: pointer;

  transform: rotate(${({ $isOpen }) => ($isOpen ? '180deg' : '0deg')});
  transition: transform 0.3s ease-in-out;

  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const UserChevronBox = styled.div<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;

  margin-left: auto;
  cursor: pointer;

  transform: rotate(${({ $isOpen }) => ($isOpen ? '180deg' : '0deg')});
  transition: transform 0.3s ease-in-out;

  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const UserInfoBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  > .basic-info-box {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;

    > p {
      font-weight: 500;
      font-size: 16px;
      line-height: 22px;

      color: rgba(70, 71, 76, 1);

      &[data-type='male'] {
        font-weight: 400;
        font-size: 14px;
        line-height: 20px;

        color: rgba(48, 54, 209, 1);
      }

      &[data-type='female'] {
        font-weight: 400;
        font-size: 14px;
        line-height: 20px;

        color: rgba(162, 109, 231, 1);
      }

      &[data-type='name'] {
        font-weight: 500;
        font-size: 16px;
        line-height: 22px;
      }

      &[data-type='phone'] {
        font-weight: 500;
        font-size: 16px;
        line-height: 22px;
      }
    }
  }

  > p[data-type='opt-name'] {
    font-weight: 400;
    font-size: 15px;
    line-height: 22px;

    color: rgba(152, 155, 162, 1);
  }
`;

const CheckAttendanceBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;

  margin-left: auto;

  min-width: 52px;

  > p {
    color: #989ba2;
    font-weight: 500;
    font-size: 16px;
    line-height: 22px;

    margin-bottom: 0;
  }

  > p[data-type='ABSENT'] {
    color: #ff4242;
  }

  > p[data-type='USED'] {
    color: #0071e9;
  }
`;

const CancelRequestBox = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  margin-top: 16px;

  p {
    font-weight: 500;
    font-size: 13px;
    line-height: 18px;
    color: #ff4242;
  }
`;

const VerticalSpaceBox = styled.div`
  height: 24px;
`;

interface BookingItemsFilter {
  page: number;
  size: number;
  filter: {
    hostId: string;
    customerSearchKeyword: string | null;
    scheduleId?: string;
    unscheduled?: boolean;
    term?: {
      startedAt?: number;
      endedAt?: number;
    };
    termTypes?: string[];
  };
}

interface TicketCardProps {
  date: string;
  openDetailModal: (item: UnfixedTicket | FixedTicket) => void;
  selectItemHandler: (item: UnfixedTicket | FixedTicket) => void;
  selectedBookingList: BookingEdge[];
  bookingItemsFilter: BookingItemsFilter;
  selectedItemId: string | null; // 부킹리스트에서 선택한 유저 id
  handleShowUserCardDetail: (itemId: string) => void; // 부킹리스트에서 선택한 유저 id
  bookingListLoading: boolean;
}

const FixedTicketCard = ({
  ticketData,
  date,
  selectedTicket,
  selectedItemId,
  handleShowUserCardDetail,
  selectItemHandler,
  selectedBookingList,
  openDetailModal,
  bookingItemsFilter,
  bookingListLoading,
}: TicketCardProps & {
  ticketData: FixedTicket;
  selectedTicket: FixedTicket | null;
}) => {
  const isMobile = useIsMobile();

  const parsedDate = dayjs(date, 'YYYY년 M월 D일 dddd');
  const reformedDate = parsedDate.format('M월 D일(dd)');

  const endDateTimeStamp = ticketData.term.endedAt;
  const startDateTimeStamp = ticketData.term.startedAt;

  const endDay = dayjs(new Date(endDateTimeStamp));
  const startDay = dayjs(new Date(startDateTimeStamp));
  const reformedEndDate = endDay.format('M월 D일(dd)');

  const isSameDay =
    startDay.format('YYYY-MM-DD') === endDay.format('YYYY-MM-DD');

  const dayDiff = endDay.diff(startDay, 'day');
  const hourDiff = endDay.diff(startDay, 'hour');

  const isExactlySameDay =
    endDateTimeStamp ===
    startDateTimeStamp; /* 대부분 조율형인경우. 예약확정시 duration을 0으로 보내기때문에 */

  const isAllCheckedAttendance = selectedBookingList.every(
    ({ node: data }) =>
      data.status === BookingState.USED || data.status === BookingState.ABSENT,
  );

  const attendedCount = selectedBookingList.reduce((acc, { node: data }) => {
    const { applied, confirmed, used, absent } = data.counts;

    return acc + used;
  }, 0);

  const absentedCount = selectedBookingList.reduce((acc, { node: data }) => {
    const { applied, confirmed, used, absent } = data.counts;

    return acc + absent;
  }, 0);

  const isSelectedUser =
    selectedTicket?.product?.id === ticketData?.product?.id &&
    selectedTicket?.term?.startedAt === ticketData?.term?.startedAt &&
    selectedTicket.id === ticketData?.id;

  const hasNoOnePurchased =
    (ticketData.allSalesCount?.sale ?? ticketData.counts.sale) === 0; /* 지정형은 아무도 안샀을 경우가 있음 */

  const sumUpTotalCount = selectedBookingList.reduce((acc, { node: cur }) => {
    const isCanceled = cur.status === BookingState.CANCELED;

    if (isCanceled) {
      return acc;
    }

    return cur.items.reduce((acc, cur2) => {
      if (cur2.tickets.length > 1) {
        const realTicketList = cur2.tickets.filter(ticket => {
          return ticket?.scheduleTerm?.startedAt === startDateTimeStamp;
        });

        return acc + realTicketList.length;
      }

      return (
        acc +
        cur2.counts.applied +
        cur2.counts.confirmed +
        cur2.counts.used +
        cur2.counts.absent
      );
    }, acc);
  }, 0);

  const isConfirmed = selectedTicket?.saleTerm?.endedAt < dayjs().valueOf();

  return (
    <TicketBox>
      {isMobile ? (
        <MobileBriefInfoBox
          onClick={() => {
            if (hasNoOnePurchased) {
              return;
            }
            selectItemHandler(ticketData);
          }}
        >
          <ProductStatusBadge item={ticketData} />

          <div>
            <div className="period-box">
              <MobileDateContainer>
                <div data-type="date-box">
                  <p data-type="date">{reformedDate}</p>
                  <p data-type="time">
                    {' '}
                    {dayjs(ticketData.term.startedAt).format('H:mm')} ~{' '}
                  </p>
                </div>

                <div data-type="date-box">
                  {!isSameDay && (
                    <p data-type="date">{`${endDay.format(
                      `${dayDiff > 4 ? 'M월 D일(ddd)' : 'D일(ddd)'}`,
                    )} `}</p>
                  )}

                  <p data-type="time">
                    {isExactlySameDay
                      ? ''
                      : isSameDay
                      ? endDay.format('H:mm')
                      : `${endDay.format('H:mm')}`}{' '}
                    {!isExactlySameDay &&
                      (!isSameDay && dayDiff + 1 > 1
                        ? `(${dayDiff + 1}일)`
                        : `(${hourDiff}시간)`)}
                  </p>
                </div>

                <ChevronBox $isOpen={ticketData?.id === selectedTicket?.id}>
                  <IconChevronDown
                    width={24}
                    height={24}
                    color={hasNoOnePurchased ? '#DBDCDF' : '#414B56'}
                  />
                </ChevronBox>
              </MobileDateContainer>
              <p data-type="title">{ticketData.product.title}</p>
            </div>

            <img
              src={ticketData.product.headerContents[0].content.thumbnail}
              alt={ticketData.product.title}
            />
          </div>
        </MobileBriefInfoBox>
      ) : (
        <BriefInfoBox
          onClick={() => {
            if (hasNoOnePurchased) {
              return;
            }
            selectItemHandler(ticketData);
          }}
        >
          <img
            src={ticketData.product.headerContents[0].content.thumbnail}
            alt={ticketData.product.title}
          />

          <div>
            <div className="period-box">
              <ProductStatusBadge item={ticketData} />
              <p data-type="date">{reformedDate}</p>
              <p data-type="time">
                {' '}
                {dayjs(ticketData.term.startedAt).format('H:mm')} ~{' '}
                {isExactlySameDay
                  ? ''
                  : isSameDay
                  ? endDay.format('H:mm')
                  : `${endDay.format(
                      `${dayDiff > 4 ? 'M월 D일(ddd)' : 'D일(ddd)'}`,
                    )} ${endDay.format('H:mm')}`}{' '}
                {!isExactlySameDay &&
                  (!isSameDay && dayDiff + 1 > 1
                    ? `(${dayDiff + 1}일)`
                    : `(${hourDiff}시간)`)}
              </p>
            </div>

            <p data-type="title">{ticketData.product.title}</p>
          </div>

          <ChevronBox $isOpen={ticketData?.id === selectedTicket?.id}>
            <IconChevronDown
              width={24}
              height={24}
              color={hasNoOnePurchased ? '#DBDCDF' : '#414B56'}
            />
          </ChevronBox>
        </BriefInfoBox>
      )}

      {isSelectedUser &&
        (bookingListLoading ? (
          <ListLoadingSpinner minHeight={'192px'} />
        ) : (
          selectedBookingList?.length > 0 && (
            <UserListBox>
              <BookingOverviewBox>
                <div
                  className={`booking-count-box ${
                    bookingItemsFilter?.filter.customerSearchKeyword &&
                    'searched'
                  }`}
                >
                  {bookingItemsFilter?.filter.customerSearchKeyword ? (
                    `검색결과: ${sumUpTotalCount}건`
                  ) : isAllCheckedAttendance ? (
                    <>
                      참석: {attendedCount}건<span>{'\u2502'}</span>
                      불참: {absentedCount}건
                    </>
                  ) : isConfirmed ? (
                    `총 예약확정: ${sumUpTotalCount}건`
                  ) : (
                    `총 예약대기: ${sumUpTotalCount}건`
                  )}
                </div>

                <div
                  className="detail-view-box"
                  onClick={() => openDetailModal(ticketData)}
                >
                  자세히보기{' '}
                  <IconChevronDown width={18} height={18} color={'#0071E9'} />
                </div>
              </BookingOverviewBox>

              {selectedBookingList.map(({ node: data }, idx) => {
                const attendanceResult = data.status;
                const hasCheckedAttendance = [
                  BookingState.ABSENT,
                  BookingState.USED,
                ].includes(attendanceResult);

                const transactionItemList = data.order.transactions
                  .map(data => {
                    if (data.items.length > 0) {
                      return data.items
                        .map(item => ({
                          [item.id]: data.actionStatus,
                        }))
                        .flat();
                    }
                  })
                  .flat();

                const orderTrxData = transactionItemList.reduce((acc, item) => {
                  const key = Object.keys(item)[0];
                  const value = item[key];
                  return { ...acc, [key]: value };
                }, {});

                const bookingOrderData = data.order.items.find(
                  item => item.id === data.id,
                );

                const dependedItemList = bookingOrderData.dependedItems.map(
                  dependedItem => {
                    return dependedItem.id;
                  },
                );
                const canceledItem = dependedItemList.some(
                  itemId =>
                    orderTrxData[itemId] ===
                    OrderTransactionActionStatus.CANCELLATION_COMPLETED,
                );

                const isCanceled = data.status === BookingState.CANCELED;

                const ticketCounts =
                  data.tickets.filter(
                    ticket => ticket.status !== TicketState.CANCELED,
                  ).length || 0;

                return (
                  !isCanceled && (
                    <UserCardBox key={`fixed-user-card-${data.id}`}>
                      <UserBriefInfoBox
                        onClick={() => {
                          handleShowUserCardDetail(data.id);
                        }}
                      >
                        <ImgComponent
                          src={data.customer?.profileImage?.uri}
                          alt="thumbnail"
                          style={{
                            minWidth: '52px',
                            minHeight: '52px',
                            maxWidth: '52px',
                            maxHeight: '52px',

                            borderRadius: '16px',
                          }}
                        />

                        <UserInfoBox>
                          <div className="basic-info-box">
                            {data.customer?.gender && (
                              <>
                                <p
                                  data-type={
                                    data.customer?.gender === 'F'
                                      ? 'female'
                                      : 'male'
                                  }
                                >
                                  {data.customer?.gender === 'F'
                                    ? '여자'
                                    : '남자'}
                                </p>

                                {`\u2022`}
                              </>
                            )}

                            <p data-type="name">{data.customer?.nickname}</p>

                            {data.customer?.mobileNumber && (
                              <>
                                {`\u2022`}
                                <p data-type="phone">
                                  {formatPhoneNumber(
                                    data.customer?.mobileNumber,
                                  )}
                                </p>
                              </>
                            )}

                            <ClipboardCopy
                              value={data.customer?.mobileNumber}
                            />

                            {hasCheckedAttendance && (
                              <UserChevronBox
                                $isOpen={selectedItemId === data.id}
                              >
                                <IconChevronDown
                                  width={24}
                                  height={24}
                                  color={'rgba(112, 115, 124, 1)'}
                                />
                              </UserChevronBox>
                            )}
                          </div>

                          {selectedItemId === data.id ? (
                            <></>
                          ) : (
                            <p data-type="opt-name">
                              {data.info?.name}
                              {/* {data.items.length > 1
                                ? ` 외 ${data.items.length - 1}개 (총 ${
                                    data.items.length
                                  }건)`
                                : ''} */}
                              {ticketCounts > 1
                                ? ` 외 ${
                                    ticketCounts - 1
                                  }개 (총 ${ticketCounts}건)`
                                : ''}
                            </p>
                          )}
                        </UserInfoBox>

                        <CheckAttendanceBox>
                          {[BookingState.ABSENT, BookingState.USED].includes(
                            attendanceResult,
                          ) ? (
                            <>
                              {attendanceResult === BookingState.ABSENT ? (
                                <IconAbsent width={20} height={20} />
                              ) : (
                                <IconCheckInCircle
                                  width={20}
                                  height={20}
                                  color={'#0071E9'}
                                />
                              )}
                              <p data-type={attendanceResult}>
                                {attendanceResult === BookingState.ABSENT
                                  ? '불참'
                                  : '참석'}
                              </p>
                            </>
                          ) : (
                            <UserChevronBox
                              $isOpen={selectedItemId === data.id}
                            >
                              <IconChevronDown
                                width={24}
                                height={24}
                                color={'rgba(112, 115, 124, 1)'}
                              />
                            </UserChevronBox>
                          )}
                        </CheckAttendanceBox>
                      </UserBriefInfoBox>

                      {bookingOrderData.dependedItems.length > 0 && (
                        <CancelRequestBox>
                          <IconWarning />
                          <p>
                            {canceledItem
                              ? '취소된 예약건이 있어요.'
                              : '주문 취소가 요청된 예약건이 있어요.'}
                          </p>
                        </CancelRequestBox>
                      )}

                      {/* {selectedItemId === data.id && <VerticalSpaceBox />} */}

                      {selectedItemId === data.id && (
                        <>
                          <VerticalSpaceBox />
                          <ListViewBookedUserDetail
                            data={data}
                            orderTrxData={orderTrxData}
                            startDateTimeStamp={startDateTimeStamp}
                          />
                        </>
                      )}
                    </UserCardBox>
                  )
                );
              })}
            </UserListBox>
          )
        ))}
    </TicketBox>
  );
};

const UnfixedTicketCard = ({
  ticketData,
  date,
  openDetailModal,
  selectItemHandler,
  selectedBookingList,
  bookingItemsFilter,
  selectedTicket,
  selectedItemId,
  handleShowUserCardDetail,
  bookingListLoading,
}: TicketCardProps & {
  ticketData: UnfixedTicket;
  selectedTicket: UnfixedTicket | null;
}) => {
  const isMobile = useIsMobile();

  const [isImageError, setIsImageError] = useState(false);

  const parsedDate = dayjs(date, 'YYYY년 M월 D일 dddd');
  const reformedDate = parsedDate.format('M월 D일(dd)');

  const endDateTimeStamp = ticketData.scheduleTerm.endedAt;
  const startDateTimeStamp = ticketData.scheduleTerm.startedAt;

  const endDay = dayjs(new Date(endDateTimeStamp));
  const startDay = dayjs(new Date(startDateTimeStamp));

  const isSameDay =
    startDay.format('YYYY-MM-DD') === endDay.format('YYYY-MM-DD');

  const dayDiff = endDay.diff(startDay, 'day');
  const hourDiff = endDay.diff(startDay, 'hour');

  const isExactlySameDay =
    endDateTimeStamp ===
    startDateTimeStamp; /* 대부분 조율형인경우. 예약확정시 duration을 0으로 보내기때문에 */

  const isAllCheckedAttendance = selectedBookingList.every(
    ({ node: data }) =>
      data.status === BookingState.USED || data.status === BookingState.ABSENT,
  );

  const attendedCount = selectedBookingList.reduce((acc, { node: data }) => {
    const { applied, confirmed, used, absent } = data.counts;

    return acc + used;
  }, 0);

  const absentedCount = selectedBookingList.reduce((acc, { node: data }) => {
    const { applied, confirmed, used, absent } = data.counts;

    return acc + absent;
  }, 0);

  const isSelectedUser =
    selectedTicket?.bookingItem?.booking?.info?.product?.id ===
      ticketData?.bookingItem?.booking?.info?.product?.id &&
    selectedTicket?.scheduleTerm?.startedAt ===
      ticketData?.scheduleTerm?.startedAt;

  const sumUpTotalCount = selectedBookingList.reduce((acc, { node: cur }) => {
    const isCanceled = cur.status === BookingState.CANCELED;

    if (isCanceled) {
      return acc;
    }

    return cur.items.reduce((acc, cur2) => {
      if (cur2.tickets.length > 1) {
        const realTicketList = cur2.tickets.filter(ticket => {
          return ticket?.scheduleTerm?.startedAt === startDateTimeStamp;
        });

        return acc + realTicketList.length;
      }

      return (
        acc +
        cur2.counts.applied +
        cur2.counts.confirmed +
        cur2.counts.used +
        cur2.counts.absent
      );
    }, acc);
  }, 0);

  return (
    <TicketBox>
      {isMobile ? (
        <MobileBriefInfoBox
          onClick={() => {
            selectItemHandler(ticketData);
          }}
        >
          <ProductStatusBadge item={ticketData} />

          <div>
            <div className="period-box">
              <MobileDateContainer>
                <div data-type="date-box">
                  <p data-type="date">{reformedDate}</p>
                  <p data-type="time">
                    {' '}
                    {dayjs(ticketData.scheduleTerm.startedAt).format(
                      'H:mm',
                    )} ~{' '}
                  </p>
                </div>

                <div data-type="date-box">
                  {!isSameDay && (
                    <p data-type="date">{`${endDay.format(
                      `${dayDiff > 4 ? 'M월 D일(ddd)' : 'D일(ddd)'}`,
                    )} `}</p>
                  )}

                  <p data-type="time">
                    {isExactlySameDay
                      ? ''
                      : isSameDay
                      ? endDay.format('H:mm')
                      : `${endDay.format('H:mm')}`}{' '}
                    {!isExactlySameDay &&
                      (!isSameDay && dayDiff + 1 > 1
                        ? `(${dayDiff + 1}일)`
                        : `(${hourDiff}시간)`)}
                  </p>
                </div>

                <ChevronBox $isOpen={ticketData?.id === selectedTicket?.id}>
                  <IconChevronDown color={'#414B56'} width={24} height={24} />
                </ChevronBox>
              </MobileDateContainer>

              <p data-type="title">
                {ticketData.bookingItem.booking.info.product.title}
              </p>
            </div>

            <img
              src={
                ticketData.bookingItem.booking.info.product.content.thumbnail
              }
              alt={ticketData.bookingItem.booking.info.product.title}
            />
          </div>
        </MobileBriefInfoBox>
      ) : (
        <BriefInfoBox
          onClick={() => {
            selectItemHandler(ticketData);
          }}
        >
          <img
            src={ticketData.bookingItem.booking.info.product.content.thumbnail}
            alt={ticketData.bookingItem.booking.info.product.title}
          />

          <div>
            <div className="period-box">
              <ProductStatusBadge item={ticketData} />
              <p data-type="date">{reformedDate}</p>
              <p data-type="time">
                {' '}
                {dayjs(ticketData.scheduleTerm.startedAt).format('H:mm')} ~{' '}
                {isExactlySameDay
                  ? ''
                  : isSameDay
                  ? endDay.format('H:mm')
                  : `${endDay.format(
                      `${dayDiff > 4 ? 'M월 D일(ddd)' : 'D일(ddd)'}`,
                    )} ${endDay.format('H:mm')}`}{' '}
                {!isExactlySameDay &&
                  (!isSameDay && dayDiff + 1 > 1
                    ? `(${dayDiff + 1}일)`
                    : `(${hourDiff}시간)`)}
              </p>
            </div>

            <p data-type="title">
              {ticketData.bookingItem.booking.info.product.title}
            </p>
          </div>

          <ChevronBox $isOpen={ticketData?.id === selectedTicket?.id}>
            <IconChevronDown color={'#414B56'} width={24} height={24} />
          </ChevronBox>
        </BriefInfoBox>
      )}

      {isSelectedUser &&
        (bookingListLoading ? (
          <ListLoadingSpinner minHeight={'192px'} />
        ) : (
          selectedBookingList?.length > 0 && (
            <UserListBox>
              <BookingOverviewBox>
                <div
                  className={`booking-count-box ${
                    bookingItemsFilter?.filter.customerSearchKeyword &&
                    'searched'
                  }`}
                >
                  {bookingItemsFilter?.filter.customerSearchKeyword ? (
                    `검색결과: ${sumUpTotalCount}건`
                  ) : isAllCheckedAttendance ? (
                    <>
                      참석: {attendedCount}건<span>{'\u2502'}</span>
                      불참: {absentedCount}건
                    </>
                  ) : (
                    `총 예약확정: ${sumUpTotalCount}건`
                  )}
                </div>

                <div
                  className="detail-view-box"
                  onClick={() => openDetailModal(ticketData)}
                >
                  자세히보기{' '}
                  <IconChevronDown width={18} height={18} color={'#0071E9'} />
                </div>
              </BookingOverviewBox>

              {selectedBookingList.map(({ node: data }, idx) => {
                const attendanceResult = data.status;
                const hasCheckedAttendance = [
                  BookingState.ABSENT,
                  BookingState.USED,
                ].includes(attendanceResult);

                const transactionItemList = data.order.transactions
                  .map(data => {
                    if (data.items.length > 0) {
                      return data.items
                        .map(item => ({
                          [item.id]: data.actionStatus,
                        }))
                        .flat();
                    }
                  })
                  .flat();

                const orderTrxData = transactionItemList.reduce((acc, item) => {
                  const key = Object.keys(item)[0];
                  const value = item[key];
                  return { ...acc, [key]: value };
                }, {});

                const bookingOrderData = data.order.items.find(
                  item => item.id === data.id,
                );

                const dependedItemList = bookingOrderData.dependedItems.map(
                  dependedItem => {
                    return dependedItem.id;
                  },
                );
                const canceledItem = dependedItemList.some(
                  itemId =>
                    orderTrxData[itemId] ===
                    OrderTransactionActionStatus.CANCELLATION_COMPLETED,
                );

                const ticketCounts =
                  data.tickets.filter(
                    ticket => ticket.status !== TicketState.CANCELED,
                  ).length || 0;

                return (
                  <UserCardBox key={`unfixed-user-card-${data.id}`}>
                    <UserBriefInfoBox
                      onClick={() => {
                        handleShowUserCardDetail(data.id);
                      }}
                    >
                      <ImgComponent
                        src={data.customer?.profileImage?.uri}
                        style={{
                          minWidth: '52px',
                          minHeight: '52px',
                          maxWidth: '52px',
                          maxHeight: '52px',

                          borderRadius: '16px',
                        }}
                        alt="thumbnail"
                      />

                      <UserInfoBox>
                        <div className="basic-info-box">
                          {data.customer?.gender && (
                            <>
                              <p
                                data-type={
                                  data.customer?.gender === 'F'
                                    ? 'female'
                                    : 'male'
                                }
                              >
                                {data.customer?.gender === 'F'
                                  ? '여자'
                                  : '남자'}
                              </p>

                              {`\u2022`}
                            </>
                          )}

                          <p data-type="name">{data.customer?.nickname}</p>

                          {data.customer?.mobileNumber && (
                            <>
                              {`\u2022`}
                              <p data-type="phone">
                                {formatPhoneNumber(data.customer?.mobileNumber)}
                              </p>
                            </>
                          )}

                          <ClipboardCopy value={data.customer?.mobileNumber} />

                          {hasCheckedAttendance && (
                            <UserChevronBox
                              $isOpen={selectedItemId === data.id}
                            >
                              <IconChevronDown
                                width={24}
                                height={24}
                                color={'rgba(112, 115, 124, 1)'}
                              />
                            </UserChevronBox>
                          )}
                        </div>

                        {selectedItemId === data.id ? (
                          <></>
                        ) : (
                          <p data-type="opt-name">
                            {data.info?.name}
                            {/*  */}
                            {/* {data.items.length > 1
                              ? ` 외 ${data.items.length - 1}개 (총 ${
                                  data.items.length
                                }건)`
                              : ''} */}

                            {ticketCounts > 1
                              ? ` 외 ${
                                  ticketCounts - 1
                                }개 (총 ${ticketCounts}건)`
                              : ''}
                          </p>
                        )}
                      </UserInfoBox>

                      <CheckAttendanceBox>
                        {[BookingState.ABSENT, BookingState.USED].includes(
                          attendanceResult,
                        ) ? (
                          <>
                            {attendanceResult === BookingState.ABSENT ? (
                              <IconAbsent width={20} height={20} />
                            ) : (
                              <IconCheckInCircle
                                width={20}
                                height={20}
                                color={'#0071E9'}
                              />
                            )}
                            <p data-type={attendanceResult}>
                              {attendanceResult === BookingState.ABSENT
                                ? '불참'
                                : '참석'}
                            </p>
                          </>
                        ) : (
                          <UserChevronBox $isOpen={selectedItemId === data.id}>
                            <IconChevronDown
                              width={24}
                              height={24}
                              color={'rgba(112, 115, 124, 1)'}
                            />
                          </UserChevronBox>
                        )}
                      </CheckAttendanceBox>
                    </UserBriefInfoBox>

                    {bookingOrderData.dependedItems.length > 0 && (
                      <CancelRequestBox>
                        <IconWarning />
                        <p>
                          {canceledItem
                            ? '취소된 예약건이 있어요.'
                            : '주문 취소가 요청된 예약건이 있어요.'}
                        </p>
                      </CancelRequestBox>
                    )}

                    {selectedItemId === data.id && (
                      <ListViewBookedUserDetail
                        data={data}
                        orderTrxData={orderTrxData}
                        startDateTimeStamp={startDateTimeStamp}
                      />
                    )}
                  </UserCardBox>
                );
              })}
            </UserListBox>
          )
        ))}
    </TicketBox>
  );
};

interface Props {
  ticket: FixedTicket | UnfixedTicket;
  isFixedTicket: boolean;
  date: string;
  selectedTicket: FixedTicket | UnfixedTicket | null;
  selectedBookingList: BookingEdge[];
  bookingItemsFilter: BookingItemsFilter;
  openBookingDetailHandler: (item: FixedTicket | UnfixedTicket) => void;
  selectItemHandler: (item: FixedTicket | UnfixedTicket) => void;
  bookingListLoading: boolean;
}

function ListViewTicketCard({
  ticket,
  isFixedTicket,
  date,
  selectedTicket,
  selectedBookingList,
  bookingItemsFilter,
  openBookingDetailHandler,
  selectItemHandler,
  bookingListLoading,
}: Props) {
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  const handleShowUserCardDetail = (itemId: string) => {
    setSelectedItemId(pre => {
      if (itemId === pre) {
        return null;
      }

      return itemId;
    });
  };

  return (
    <>
      {isFixedTicket ? (
        <FixedTicketCard
          ticketData={ticket as FixedTicket}
          date={date}
          selectItemHandler={openBookingDetailHandler}
          selectedTicket={selectedTicket as FixedTicket}
          selectedBookingList={selectedBookingList}
          openDetailModal={selectItemHandler}
          bookingItemsFilter={bookingItemsFilter}
          selectedItemId={selectedItemId}
          handleShowUserCardDetail={handleShowUserCardDetail}
          bookingListLoading={bookingListLoading}
        />
      ) : (
        <UnfixedTicketCard
          ticketData={ticket as UnfixedTicket}
          date={date}
          selectItemHandler={openBookingDetailHandler}
          selectedTicket={selectedTicket as UnfixedTicket}
          selectedBookingList={selectedBookingList}
          openDetailModal={selectItemHandler}
          bookingItemsFilter={bookingItemsFilter}
          selectedItemId={selectedItemId}
          handleShowUserCardDetail={handleShowUserCardDetail}
          bookingListLoading={bookingListLoading}
        />
      )}
    </>
  );
}

export default ListViewTicketCard;
